import { useQuery } from '@tanstack/react-query';
import api from '@/lib/axios';
import type { PerfilClinica } from '@/lib/perfilClinicaPresets';

export type { PerfilClinica };

/**
 * Debe reflejar MODULOS en backend/src/common/constants/modulos.constant.ts —
 * solo se listan aquí los módulos que el frontend necesita mostrar/ocultar por perfil de
 * clínica. Incluye los moduleKeys clínicos de la Ficha del Paciente/Consulta, migrados desde
 * el eje de especialidad del médico — ver
 * .planning/specs/gating-ficha-paciente-por-perfil-clinica.md.
 */
const MODULOS_PERFILES: Record<string, PerfilClinica[] | 'all'> = {
  odontograma: ['dental', 'multidisciplinaria'],
  periodontograma: ['dental', 'multidisciplinaria'],

  signos_vitales_nucleo: 'all',
  signos_vitales_deportivo: ['medica_general', 'fisioterapia', 'nutricion', 'especialidad', 'multidisciplinaria'],
  diagnosticos_lectura: 'all',
  diagnosticos_emitir: ['dental', 'medica_general', 'especialidad', 'multidisciplinaria', 'psicologia', 'oftalmologia', 'dermatologia'],
  examen_fisico: ['medica_general', 'fisioterapia', 'especialidad', 'multidisciplinaria', 'dermatologia'],
  mapa_topografico: ['medica_general', 'fisioterapia', 'especialidad', 'multidisciplinaria', 'dermatologia'],
  formularios_dinamicos: 'all',
  recetas_emitir: ['dental', 'medica_general', 'especialidad', 'multidisciplinaria', 'psicologia', 'oftalmologia', 'dermatologia'],
  medicamentos_lectura: 'all',
  estudios_lectura: 'all',
  estudios_emitir: ['dental', 'medica_general', 'especialidad', 'multidisciplinaria', 'oftalmologia', 'dermatologia'],
};

/**
 * Misma lógica y orden que ModuleAccessGuard (capa 2: perfil de clínica, capa 3: override
 * manual). Sin perfil configurado, la capa 2 no bloquea (fail-open); el override solo
 * bloquea con `false` explícito — su ausencia significa habilitado.
 */
export function moduloAplica(
  moduloKey: string,
  perfilTipo?: PerfilClinica | null,
  modulosHabilitados?: Record<string, boolean>,
): boolean {
  const perfiles = MODULOS_PERFILES[moduloKey];
  if (perfiles && perfiles !== 'all' && perfilTipo && !perfiles.includes(perfilTipo)) return false;
  if (modulosHabilitados?.[moduloKey] === false) return false;
  return true;
}

export function useConfiguracionClinica() {
  const { data, isLoading } = useQuery({
    queryKey: ['configuracion'],
    queryFn: () => api.get('/api/configuracion').then(r => r.data),
    staleTime: 5 * 60 * 1000,
  });

  return {
    perfilTipo: data?.perfilTipo as PerfilClinica | undefined,
    modulosHabilitados: (data?.modulosHabilitadosJson ?? {}) as Record<string, boolean>,
    isLoading,
  };
}
